import clsx from "clsx";
import {
  InputHTMLAttributes,
  LabelHTMLAttributes,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  FormProvider,
  RegisterOptions,
  useForm,
  useFormContext,
} from "react-hook-form";
import { parsePercentValue, PercentFormatter } from "../../util/formatter";
import { OutputValues } from "../../util/types";
import Accordion from "../accordion/Accordion";
import AccordionItem from "../accordion/AccordionItem";
import Hint from "./Hint";
import styles from "./RechnerForm.module.scss";

export type RechnerFormProps = {
  onChange: (values: OutputValues) => void;
  defaultValues: OutputValues;
};

type FormData = Omit<
  OutputValues,
  | "inflation"
  | "performance_savings"
  | "performance_withdrawal"
  | "statutory_pension_increase"
  | "rent_increase"
> & {
  inflation_percent: string;
  performance_savings_percent: string;
  performance_withdrawal_percent: string;
  statutory_pension_increase_percent: string;
  rent_increase_percent: string;
};

function formatPercentString(value: number) {
  const parts = PercentFormatter.formatToParts(value);
  return parts
    .filter((p) => p.type !== "percentSign")
    .map((p) => p.value)
    .join("")
    .trim();
}

function RechnerForm({
  defaultValues: {
    inflation: dv_inflation,
    performance_savings: dv_performance_savings,
    performance_withdrawal: dv_performance_withdrawal,
    statutory_pension_increase: dv_statutory_pension_increase,
    rent_increase: dv_rent_increase,
    ...dv
  },
  onChange,
}: RechnerFormProps) {
  const triggerOnChangeTimeoutId = useRef<number | null>(null);
  const formDefaultValues: FormData = {
    ...dv,
    inflation_percent: formatPercentString(dv_inflation),
    performance_savings_percent: formatPercentString(dv_performance_savings),
    performance_withdrawal_percent: formatPercentString(
      dv_performance_withdrawal
    ),
    statutory_pension_increase_percent: formatPercentString(
      dv_statutory_pension_increase
    ),
    rent_increase_percent: formatPercentString(dv_rent_increase),
  };

  const formMethods = useForm<FormData>({
    defaultValues: formDefaultValues,
    reValidateMode: "onChange",
  });
  const { handleSubmit: useFormSubmit, watch } = formMethods;

  const triggerOnChange = useCallback(
    ({
      inflation_percent,
      performance_savings_percent,
      performance_withdrawal_percent,
      statutory_pension_increase_percent,
      rent_increase_percent,
      ...values
    }: FormData) => {
      Object.entries(values).forEach(([key, v]) => {
        const valid = !(
          v == null ||
          (typeof v === "number" && Number.isNaN(v))
        );
        if (!valid) {
          // @ts-ignore
          values[key] = 0;
        }
      });
      const parsedValues = {
        ...values,
        inflation: parsePercentValue(inflation_percent),
        performance_savings: parsePercentValue(performance_savings_percent),
        performance_withdrawal: parsePercentValue(
          performance_withdrawal_percent
        ),
        statutory_pension_increase: parsePercentValue(
          statutory_pension_increase_percent
        ),
        rent_increase: parsePercentValue(rent_increase_percent),
      };
      process.env.NODE_ENV === "development" &&
        console.log(values, parsedValues);
      onChange(parsedValues);
    },
    [onChange]
  );
  const submitHandle = useFormSubmit(triggerOnChange);

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      process.env.NODE_ENV === "development" && console.log(value, name, type);
      if (triggerOnChangeTimeoutId.current) {
        window.clearTimeout(triggerOnChangeTimeoutId.current);
      }
      triggerOnChangeTimeoutId.current = window.setTimeout(() => {
        triggerOnChange(value as FormData);
      }, 450);
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [triggerOnChange, watch]);

  return (
    <FormProvider {...formMethods}>
      <form className={styles.form} onSubmit={submitHandle}>
        <Accordion initActive={["main"]}>
          <AccordionItem
            index="main"
            header="Allgemeine Daten"
            className={styles.page}
          >
            <Input
              inputProps={{
                type: "number",
                min: 1,
                step: 1,
              }}
              label="Alter"
              name="age"
              registerOptions={{
                valueAsNumber: true,
                required: "Bitte geben Sie ihr Alter an.",
              }}
            />
            <Input
              hint={"Monatliche Netto Rente"}
              inputProps={{
                type: "number",
                min: 1,
                step: 1,
              }}
              label="Wunschrente"
              name="wish_pension"
              inputAdornment={{
                content: "€",
                side: "end",
              }}
              registerOptions={{
                valueAsNumber: true,
                required: "Bitte Wunschrente angeben.",
              }}
            />
            <Input
              inputProps={{
                type: "number",
                min: 1,
                step: 1,
              }}
              hint="Achtung: Gesetzliche Rente frühestens 36 Monate vor Regelrenteneintrittsalter mit Abschlägen."
              label="Rentenalter"
              name="age_entry"
              registerOptions={{
                valueAsNumber: true,
                required: "Bitte geben Sie ihr Renten Eintrittsalter an.",
              }}
            />
            <Input
              inputProps={{
                type: "number",
                min: 1,
                step: 1,
                pattern: "\\d*",
              }}
              label="Lebenserwartung"
              name="age_till"
              registerOptions={{
                valueAsNumber: true,
                pattern: {
                  message: "Bitte valides Alter eingeben",
                  value: /^[^0][0-9]+/,
                },
                required: "Bis wann beziehen Sie Rente.",
              }}
            />
          </AccordionItem>
          <AccordionItem
            index={"eck_daten"}
            header="Eckdaten"
            className={styles.page}
          >
            <Input
              hint="Durchschnitt der letzten 20 Jahre: 1,66% p.a."
              label="Inflation"
              inputAdornment={{
                content: "%",
                side: "end",
              }}
              name="inflation_percent"
              registerOptions={{
                required: "Inflation als Prozent Zahl.",
              }}
            />
            <Input
              label="Wertentwicklung Ansparphase"
              inputAdornment={{
                content: "%",
                side: "end",
              }}
              name="performance_savings_percent"
              registerOptions={{
                required: "als Prozent Zahl",
              }}
            />
            <Input
              label="Wertentwicklung Entnahmephase"
              inputAdornment={{
                content: "%",
                side: "end",
              }}
              name="performance_withdrawal_percent"
              registerOptions={{
                required: " als Prozent Zahl",
              }}
            />
          </AccordionItem>
          <AccordionItem
            header="Private Altersvorsorge"
            index="current_services"
            className={clsx(styles.page)}
          >
            <div className={styles.row2}>
              <h3>Gesetzliche Rente</h3>
              <Input
                hint="Prognose laut Renteninformation (Achtung: Gesetzliche Rente frühestens 36 Monate vor Regelrenteneintrittsalter mit Abschlägen.)"
                inputAdornment={{
                  content: "€",
                  side: "end",
                }}
                label="gesetzliche Altersrente / Versorgungswerk"
                registerOptions={{
                  valueAsNumber: true,
                }}
                name="statutory_pension"
              />
              <Input
                label="Steigerung in %"
                hint="Jährliche Steigerung in Prozent. Rentensteigerung von 2000-2020: West 1,67% p.a. und Ost 2,26% p.a."
                inputAdornment={{
                  content: "%",
                  side: "end",
                }}
                name="statutory_pension_increase_percent"
                registerOptions={{
                  required: "Steigerung als Prozent Zahl.",
                }}
              />
            </div>
            <div className={styles.row2}>
              <h3>Private Renten</h3>
              <div className={styles.row2Content}>
                <h5>Rüruprente (Schicht I)</h5>
                <Input
                  label="derzeitiger Vertragsstand"
                  inputAdornment={{
                    content: "€",
                    side: "end",
                  }}
                  registerOptions={{
                    valueAsNumber: true,
                  }}
                  name="ruerup_pension"
                />
                <Input
                  className={styles.row2Input}
                  label="monatliche Sparrate"
                  inputAdornment={{
                    content: "€",
                    side: "end",
                  }}
                  registerOptions={{
                    valueAsNumber: true,
                  }}
                  name="ruerup_pension_monthly"
                />
              </div>
              <div className={styles.row2Content}>
                <h5>Riester/betriebliche AV (Schicht II)</h5>
                <Input
                  label="derzeitiger Vertragsstand"
                  inputAdornment={{
                    content: "€",
                    side: "end",
                  }}
                  registerOptions={{
                    valueAsNumber: true,
                  }}
                  name="riester_pension"
                />
                <Input
                  className={styles.row2Input}
                  label="monatliche Sparrate"
                  inputAdornment={{
                    content: "€",
                    side: "end",
                  }}
                  registerOptions={{
                    valueAsNumber: true,
                  }}
                  name="riester_pension_monthly"
                />
              </div>
              <div className={styles.row2Content}>
                <h5>Private Rente (Schicht III)</h5>
                <Input
                  label="derzeitiger Vertragsstand"
                  inputAdornment={{
                    content: "€",
                    side: "end",
                  }}
                  registerOptions={{
                    valueAsNumber: true,
                  }}
                  name="private_pension"
                />
                <Input
                  className={styles.row2Input}
                  label="monatliche Sparrate"
                  inputAdornment={{
                    content: "€",
                    side: "end",
                  }}
                  registerOptions={{
                    valueAsNumber: true,
                  }}
                  name="private_pension_monthly"
                />
              </div>
            </div>
          </AccordionItem>
          <AccordionItem
            index="other_investments"
            header="Andere Anlagen"
            className={styles.page}
          >
            <div className={styles.row2}>
              <h5>Mieteinnahmen zum Rentenbeginn</h5>
              <Input
                label="Mieteinnahmen"
                inputAdornment={{
                  content: "€",
                  side: "end",
                }}
                registerOptions={{
                  valueAsNumber: true,
                }}
                name="rent"
              />
              <Input
                label="Mietsteigerung"
                hint="Die durch&shy;schnittliche Mietsteigerung der letzten 20 Jahre lag in Deutschland bei 1,15%, wobei es starke regionale Abweichungen gab."
                inputAdornment={{
                  content: "%",
                  side: "end",
                }}
                registerOptions={{}}
                name="rent_increase_percent"
              />
            </div>
            <div className={styles.row2}>
              <h5>Geldanlagen</h5>
              <Input
                label="Aktueller Wert"
                inputAdornment={{
                  content: "€",
                  side: "end",
                }}
                registerOptions={{
                  valueAsNumber: true,
                }}
                name="depot"
              />
              <Input
                label="monatliche Sparrate"
                inputAdornment={{
                  content: "€",
                  side: "end",
                }}
                registerOptions={{
                  valueAsNumber: true,
                }}
                name="depot_monthly"
              />
            </div>
          </AccordionItem>
        </Accordion>
      </form>
    </FormProvider>
  );
}

type InputProps = {
  label: string;
  name: keyof FormData;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  registerOptions?: RegisterOptions<FormData>;
  inputAdornment?: {
    side?: "start" | "end";
    content: string;
  };
  hint?: string | JSX.Element;
} & Pick<LabelHTMLAttributes<HTMLLabelElement>, "className">;

function Input({
  className,
  hint,
  label,
  inputAdornment,
  inputProps,
  name,
  registerOptions,
}: InputProps) {
  const {
    register,
    formState: { errors },
  } = useFormContext<FormData>();

  return (
    <label className={className}>
      <div className={styles.labelContainer}>
        <div className={styles.label}>{label}</div>
        {hint && <Hint>{hint}</Hint>}
      </div>
      <div
        className={clsx(
          styles.inputContainer,
          inputProps && inputProps.type === "checkbox" && styles.inputCheckbox,
          inputAdornment && styles.inputAdornmentContainer,
          inputAdornment && inputAdornment.side === "start"
            ? styles.inputAdornmentStart
            : styles.inputAdornmentEnd
        )}
      >
        <input {...inputProps} {...register(name, registerOptions)} />
        {inputAdornment && (
          <span className={clsx(styles.inputAdornment)}>
            {inputAdornment.content}
          </span>
        )}
      </div>
      <span>{errors[name]?.message}</span>
    </label>
  );
}

export default RechnerForm;
